import { useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { useInvoiceSummary } from "../api/invoiceQueries";
import { LIFECYCLE_STATUS } from "../types/common";
import { V2_COLORS } from "../constants/colors";
import LoaderV2 from "@/pages/InvoiceManagement/components/LoaderV2";

interface KpiCardProps {
  title: string;
  count: number;
  description: string;
  color: string;
  onView: () => void;
}

function KpiCard({ title, count, description, color, onView }: KpiCardProps) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: "8px",
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: 130,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Typography fontWeight={600} fontSize="15px" sx={{ maxWidth: "70%" }}>
          {title}
        </Typography>
        <Typography fontWeight={700} fontSize="24px" color={color}>
          {count}
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" mt={1} fontSize="13px">
        {description}
      </Typography>
      <Typography
        component="span"
        sx={{
          color: V2_COLORS.primary,
          fontWeight: 600,
          fontSize: "13px",
          cursor: "pointer",
          mt: 1,
          "&:hover": { textDecoration: "underline" },
        }}
        onClick={onView}
      >
        View
      </Typography>
    </Box>
  );
}

export default function Dashboard() {
  const [, setSearchParams] = useSearchParams();

  // Summary API doesn't support factory_code — no filter needed here
  const { data: summary, isLoading } = useInvoiceSummary();

  const navigateToInvoiceTab = useCallback(
    (status: string) => {
      setSearchParams((prev) => {
        prev.set("tab", "invoices");
        prev.set("status", status);
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams]
  );

  const statusCounts = summary?.status_counts;

  const cards = useMemo(
    () => [
      {
        title: "Awaiting Approval",
        count: statusCounts?.[LIFECYCLE_STATUS.UNDER_REVIEW] ?? 0,
        description: "Invoices pending review and approval",
        color: V2_COLORS.warning,
        status: "finance_approval_pending",
      },
      {
        title: "Exceptions",
        count: statusCounts?.[LIFECYCLE_STATUS.MATCH_EXCEPTION] ?? 0,
        description: "Invoices with match exceptions requiring resolution",
        color: V2_COLORS.error,
        status: "validation_failed",
      },
      {
        title: "Rejected",
        count: statusCounts?.[LIFECYCLE_STATUS.REJECTED] ?? 0,
        description: "Invoices rejected during review",
        color: V2_COLORS.error,
        status: "rejected",
      },
      {
        title: "GRN Pending",
        count: statusCounts?.[LIFECYCLE_STATUS.GRN_PENDING] ?? 0,
        description: "Invoices waiting for GRN linkage",
        color: V2_COLORS.warning,
        status: "grn_pending",
      },
      {
        title: "Exported",
        count: statusCounts?.[LIFECYCLE_STATUS.EXPORTED] ?? 0,
        description: "Invoices successfully exported to D365",
        color: V2_COLORS.success,
        status: "erp_success",
      },
    ],
    [statusCounts]
  );

  if (isLoading) return <LoaderV2 />;

  return (
    <Box p={3}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(4, 1fr)"
        gap={2.5}
      >
        {cards.map((card) => (
          <KpiCard
            key={card.title}
            title={card.title}
            count={card.count}
            description={card.description}
            color={card.color}
            onView={() => {
              if (card.status) {
                navigateToInvoiceTab(card.status);
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
